import type { Ticket } from './types'

/**
 * What the Counter's card keeps drawing while one of its own commands runs.
 *
 * The live view passes through states nobody should see during a command: Next
 * closes the visit (one delta: nobody at the desk) and then calls (another delta).
 * The card holds the visit it showed when the command began until the command is
 * over, or until the ticket the command called reaches the live view.
 */
export type Hold = {
  /** The visit on the card when the command began; undefined at a free desk. */
  ticket?: Ticket
  /** Next: the held visit is drawn dimmed until the new one arrives. */
  dim: boolean
  /** The ticket the finished command put at this desk, not yet in the live view. */
  awaiting?: string
}

/** How long a finished command waits for the live view to show the ticket it called. */
export const AWAIT_MS = 1500

/** The visit the card draws: the live one always wins; a hold only fills a gap. */
export function cardTicket(live: Ticket | undefined, hold: Hold | null): Ticket | undefined {
  return live ?? hold?.ticket
}

/** The ticket a command's answer put at this desk ({ ticket } called or serving here). */
export function calledTicketId(result: unknown, counterId: string): string | undefined {
  const t = (result as { ticket?: Partial<Ticket> } | null | undefined)?.ticket
  if (!t || typeof t.id !== 'string') return undefined
  if (t.calledToCounterId !== counterId) return undefined
  return t.state === 'called' || t.state === 'serving' ? t.id : undefined
}

/** The hold once its command is over: kept only while the called ticket is still on its way. */
export function holdAfter(hold: Hold | null, awaiting: string | undefined, live: Ticket | undefined): Hold | null {
  if (!hold || !awaiting || live?.id === awaiting) return null
  return { ...hold, awaiting }
}

/** Whether the held visit is drawn dimmed: Next's old visit, until the live view moves on. */
export function holdDims(hold: Hold | null, live: Ticket | undefined): boolean {
  if (!hold?.dim || !hold.ticket) return false
  return !live || live.id === hold.ticket.id
}
